import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3)
        : [],
    [article],
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center text-foreground-300">
            <i className="ri-file-unknow-line text-4xl"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-950 font-bold mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">该文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName =
    article.category === 'comprehensive'
      ? '综合实力排名'
      : article.category === 'popularity'
        ? '人气热度榜'
        : article.category === 'boxoffice'
          ? '票房成绩榜'
          : article.category === 'acting'
            ? '演技评分榜'
            : article.category === 'newcomer'
              ? '新人推荐榜'
              : '综合排行';

  return (
    <>
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        datePublished: article.publishDate,
        author: { '@type': 'Person', name: article.author },
        url: `https://readdy.ai/article/${article.id}`,
        image: article.coverImage,
      }} />
      <SchemaOrg type="WebPage" data={{
        name: article.title,
        url: `https://readdy.ai/article/${article.id}`,
        description: article.excerpt,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 pb-14 md:pb-20">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                    {article.tags[0]}
                  </span>
                  <span className="text-sm text-foreground-500">{article.publishDate}</span>
                  <span className="text-sm text-foreground-500">·</span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 font-bold mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-600">作者：{article.author}</span>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden aspect-[16/9]">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 女优排行榜`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none mb-12 text-foreground-800 leading-relaxed [&_h3]:text-xl [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-950 [&_h3]:mt-8 [&_h3]:mb-4 [&_p]:mb-4 [&_p]:leading-relaxed [&_ul]:mb-4 [&_ul]:pl-5 [&_li]:mb-1 [&_strong]:text-foreground-950"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mb-12">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <div className="border-t border-background-200/70 pt-10">
                  <h3 className="text-lg font-heading text-foreground-950 font-bold mb-6">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.id} article={ra} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}